import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSubscription } from '@/hooks/useSubscription.ts'
import { usePaymentToken } from '@/hooks/usePaymentToken.ts'
import { useWallet } from '@/hooks/useWallet.ts'
import { useProfile } from '@/hooks/useProfile.ts'
import { useContract } from '@/hooks/useContract.ts'
import { sendSubscriptionConfirmedEmailWebplatform } from '@/services/notification/webplatformEmailSenders.ts'
import {
  SubscriptionTier,
  type SubscriptionTier as SubscriptionTierType,
} from '@/services/contract/contract.types.ts'
import { cn } from '@/utils/cn.ts'

interface PlanSelectorProps {
  onSubscribed: () => void
  tiers?: SubscriptionTierType[]
  mode?: 'subscribe' | 'renew'
}

export function PlanSelector({
  onSubscribed,
  tiers = [SubscriptionTier.OneMonth, SubscriptionTier.OneYear],
  mode = 'subscribe',
}: PlanSelectorProps) {
  const { t } = useTranslation()
  const { address } = useWallet()
  const { fetchProfile } = useProfile()
  const contract = useContract()
  const {
    pricing,
    loading,
    error,
    fetchPricing,
    approveAndSubscribe,
    approveAndRenew,
  } = useSubscription()
  const { tokenInfo, fetchTokenInfo } = usePaymentToken()
  const [selectedTier, setSelectedTier] = useState<SubscriptionTierType | null>(
    null,
  )

  useEffect(() => {
    fetchPricing()
    fetchTokenInfo()
  }, [fetchPricing, fetchTokenInfo])

  const formatPrice = (price: bigint): string => {
    if (!tokenInfo) return price.toString()
    const decimals = tokenInfo.decimals
    const value = Number(price) / Math.pow(10, decimals)
    return `${value} ${tokenInfo.symbol}`
  }

  const handleSubscribe = async () => {
    if (selectedTier === null) return
    const result =
      mode === 'renew'
        ? await approveAndRenew(selectedTier)
        : await approveAndSubscribe(selectedTier)
    if (result && address) {
      try {
        const latestProfile = await fetchProfile()
        const sub = await contract.getSubscription(address as `0x${string}`)
        if (latestProfile?.email?.trim() && sub?.exists) {
          await sendSubscriptionConfirmedEmailWebplatform({
            to: latestProfile.email,
            userName: latestProfile.name ?? 'User',
            tier: Number(sub.tier),
            expiryTime: sub.expiryTime,
            transactionHash: result.hash,
            notifyEmail: latestProfile.notify_email,
          })
        }
      } catch {
        /* confirmation email is best-effort */
      }
      onSubscribed()
    }
  }

  return (
    <div className="max-w-[640px]">
      <h2 className="text-fg mb-6 text-xl font-semibold">
        {t('subscription.heading')}
      </h2>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {tiers.includes(SubscriptionTier.OneMonth) && (
          <div
            className={cn(
              'border-line bg-surface-card duration-fast hover:border-accent hover:bg-surface-card-hover cursor-pointer rounded-xl border p-8 text-center transition-all',
              selectedTier === SubscriptionTier.OneMonth &&
                'border-accent shadow-glow',
            )}
            onClick={() => setSelectedTier(SubscriptionTier.OneMonth)}
            onKeyDown={(e) =>
              e.key === 'Enter' && setSelectedTier(SubscriptionTier.OneMonth)
            }
            role="button"
            tabIndex={0}
          >
            <div className="text-fg mb-2 text-lg font-semibold">
              {t('subscription.oneMonth', { defaultValue: '1 Month' })}
            </div>
            <div className="text-accent mb-1 text-2xl font-bold">
              {pricing ? formatPrice(pricing.oneMonthPrice) : '...'}
            </div>
            <div className="text-fg-muted text-[0.8125rem]">
              {t('subscription.perPeriod')}
            </div>
          </div>
        )}

        {tiers.includes(SubscriptionTier.OneYear) && (
          <div
            className={cn(
              'border-line bg-surface-card duration-fast hover:border-accent hover:bg-surface-card-hover cursor-pointer rounded-xl border p-8 text-center transition-all',
              selectedTier === SubscriptionTier.OneYear &&
                'border-accent shadow-glow',
            )}
            onClick={() => setSelectedTier(SubscriptionTier.OneYear)}
            onKeyDown={(e) =>
              e.key === 'Enter' && setSelectedTier(SubscriptionTier.OneYear)
            }
            role="button"
            tabIndex={0}
          >
            <div className="text-fg mb-2 text-lg font-semibold">
              {t('subscription.oneYear')}
            </div>
            <div className="text-accent mb-1 text-2xl font-bold">
              {pricing ? formatPrice(pricing.oneYearPrice) : '...'}
            </div>
            <div className="text-fg-muted text-[0.8125rem]">
              {t('subscription.perPeriod')}
            </div>
          </div>
        )}
      </div>

      {tokenInfo && (
        <div className="bg-surface-muted text-fg-secondary mt-6 rounded-md p-4 text-sm">
          {t('subscription.paymentToken', {
            name: tokenInfo.name,
            symbol: tokenInfo.symbol,
          })}
        </div>
      )}

      <button
        type="button"
        className="bg-gradient-brand text-fg-inverse duration-fast mt-8 w-full cursor-pointer rounded-md border-none px-6 py-3.5 text-base font-semibold transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        disabled={selectedTier === null || loading}
        onClick={handleSubscribe}
      >
        {loading
          ? t('subscription.processing')
          : mode === 'renew'
            ? t('subscription.renewNow', { defaultValue: 'Renew Now' })
            : t('subscription.subscribeNow')}
      </button>

      {error && <div className="text-error mt-4 text-sm">{error}</div>}
    </div>
  )
}
